import { getMessageHash } from '../../utils/get_message_hash';
import { getPlainMessage } from '../../utils/get_plain_message';
import { type AppDispatch, createAppThunk } from '../types';
import { clearMessage, showMessage } from './slice';
import type { Message } from './types';

const currentMessages = new Map<string, { timerId?: number } | null>();

export const removeMessage = createAppThunk(
  (messageHash: string) => (dispatch, getState) => {
    const message = getState().messages[messageHash];

    if (!message) {
      return;
    }

    const hash = getMessageHash(message);

    currentMessages.delete(hash);

    dispatch(clearMessage(messageHash));
  },
);

const autoClose =
  (message: Message) =>
  (dispatch: AppDispatch): number | undefined => {
    if (!message.autoclose) {
      return undefined;
    }
    const messageHash = getMessageHash(message);
    return window.setTimeout(() => {
      dispatch(removeMessage(messageHash));
    }, message.timeout);
  };

const prepareMessage =
  (messageArg: Message) =>
  (dispatch: AppDispatch): Message | null => {
    const message: Message = {
      ...messageArg,
      timeout: messageArg.timeout ?? 4000,
      autoclose: messageArg.autoclose ?? true,
    };

    const plainMessage = getPlainMessage(message.text);
    if (plainMessage.trim().length === 0) {
      return null;
    }

    const hash = getMessageHash(message);

    if (currentMessages.has(hash)) {
      // Re-run timer, so the message will just stay visible longer.
      const messageProps = currentMessages.get(hash);
      if (messageProps) {
        clearTimeout(messageProps.timerId);

        const timerId = dispatch(autoClose(message));
        currentMessages.set(hash, { ...messageProps, timerId });
      }
      return null;
    }

    const timerId = dispatch(autoClose(message));
    currentMessages.set(hash, { timerId });

    return messageArg;
  };

export type MessageParams = Omit<Message, 'variant'>;

export function successMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'success' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}

export function errorMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'danger' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}

export function warningMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'warning' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}

export function infoMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'info' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}
